import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { ErrorState } from "../components/shared/ErrorState";
import { Skeleton } from "../components/ui/skeleton";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";

function printPage() {
  document.body.classList.add("printing-packing-list");
  const cleanup = () => {
    document.body.classList.remove("printing-packing-list");
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  window.print();
  setTimeout(cleanup, 1000);
}

export default function RoutePackingPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get(`/routes/${id}/packing`)
      .then(({ data }) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((e) => {
        if (!cancelled) setState({ loading: false, error: getApiErrorMessage(e, "Could not load the packing list."), data: null });
      });
    return () => { cancelled = true; };
  }, [id]);

  if (state.loading) return <div className="p-6"><Skeleton className="h-96 w-full" /></div>;
  if (state.error) return <div className="p-6"><ErrorState message={state.error} onRetry={() => navigate(0)} /></div>;

  const d = state.data;
  const rows = [...(d.items || [])].sort((a, b) => (a.delivery_order_id ?? 0) - (b.delivery_order_id ?? 0));

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6" data-testid="route-packing-print-page">
      <div className="no-print mb-4 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate(`/routes/${id}/packing`)}
          className="flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-text-main"
          data-testid="route-packing-print-back"
        >
          <ArrowLeft size={16} strokeWidth={1.75} /> Back to Packing
        </button>
        <Button variant="accent" size="sm" onClick={printPage} data-testid="route-packing-print-button">
          <Printer size={15} strokeWidth={1.75} /> Print / Save as PDF
        </Button>
      </div>

      <div className="packing-list-print rounded-xl border border-border bg-surface p-4 md:p-6">
        <div className="mb-3">
          <h1 className="font-heading text-xl font-extrabold text-text-main">Packing List — Route #{d.route_id}</h1>
          <p className="text-sm text-text-muted">
            {d.driver_name || "Unassigned"} · {rows.length} tiffin{rows.length === 1 ? "" : "s"}{d.date ? ` · ${d.date}` : ""}
          </p>
        </div>

        <table className="w-full border-collapse text-sm" data-testid="route-packing-print-table">
          <thead>
            <tr className="bg-accent text-left align-bottom">
              <th className="w-8 border border-border px-2 py-1.5 font-bold text-primary">#</th>
              <th className="border border-border px-2 py-1.5 font-bold text-primary">Name</th>
              <th className="border border-border px-2 py-1.5 font-bold text-primary">Plan</th>
              <th className="border border-border px-2 py-1.5 font-bold text-primary">Add-ons</th>
              <th className="border border-border px-2 py-1.5 font-bold text-primary">Packing List</th>
              <th className="border border-border px-2 py-1.5 font-bold text-primary">Instructions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((it) => (
              <tr key={it.id} className="align-top" data-testid={`route-packing-print-row-${it.id}`}>
                <td className="border border-border px-2 py-1.5 font-bold text-text-main">{it.delivery_order_id ?? "—"}</td>
                <td className="border border-border px-2 py-1.5 font-semibold text-text-main">{it.customer_name || "—"}</td>
                <td className="border border-border px-2 py-1.5 text-text-main">{it.plan_name || "—"}</td>
                <td className="border border-border px-2 py-1.5 text-text-main">
                  {it.extras?.length ? it.extras.join(", ") : "—"}
                </td>
                <td className="border border-border px-2 py-1.5 text-text-main">
                  {it.packing_list?.length ? it.packing_list.map((pl) => `${pl.quantity}× ${pl.label}`).join(", ") : "—"}
                </td>
                <td className="border border-border px-2 py-1.5 font-semibold text-danger">{it.packing_notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
